/**
 * Investor-Report (#1190 v2.572.0) — Liste, Editor, Generate, Approve, PDF.
 *
 * Admin-only Page. KI macht NUR Narration — alle Zahlen kommen deterministisch
 * aus dem Fact-Sheet (Anhang im PDF). Manuel kann die Narration im Editor
 * überarbeiten, dann „Speichern (Draft)" → re-rendert PDF; danach „Freigeben".
 */
import { useEffect, useMemo, useState } from 'react';

const API = import.meta.env.VITE_API_URL || '';
const MONTH_LABELS_DE = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];

function monthLabel(jahr, monat) {
  return `${MONTH_LABELS_DE[(monat || 1) - 1]} ${jahr}`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-AT', { year: '2-digit', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function lastMatureMonth() {
  const now = new Date();
  let jahr = now.getFullYear();
  let monat = now.getMonth(); // getMonth() = aktueller-1 als 0..11 → ergibt direkten Vormonat als 1..12
  if (monat === 0) { jahr -= 1; monat = 12; }
  return { jahr, monat };
}

export default function InvestorReport() {
  const [list, setList] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [narrationEdit, setNarrationEdit] = useState('');
  const [busy, setBusy] = useState(false);
  const [genJahr, setGenJahr] = useState(() => lastMatureMonth().jahr);
  const [genMonat, setGenMonat] = useState(() => lastMatureMonth().monat);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  async function loadList() {
    setLoadingList(true);
    try {
      const res = await fetch(`${API}/api/investor/reports?limit=60`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setList(data.rows || []);
      }
    } finally { setLoadingList(false); }
  }

  async function loadDetail(id) {
    setLoadingDetail(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/investor/reports/${id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setDetail(data);
        setNarrationEdit(data.ki_narration || '');
      } else {
        setError('Report konnte nicht geladen werden.');
      }
    } finally { setLoadingDetail(false); }
  }

  useEffect(() => { loadList(); }, []);
  useEffect(() => { if (activeId) loadDetail(activeId); }, [activeId]);

  async function handleGenerate() {
    setBusy(true); setError(null); setInfo(null);
    try {
      const res = await fetch(`${API}/api/investor/reports/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ jahr: genJahr, monat: genMonat }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Generate-Fehler');
      setInfo(`Report ${monthLabel(genJahr, genMonat)} erzeugt${data.narration_error ? ' (Narration-Fehler: ' + data.narration_error + ')' : ''}.`);
      await loadList();
      setActiveId(data.id);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function handleSaveNarration() {
    if (!detail) return;
    setBusy(true); setError(null); setInfo(null);
    try {
      const res = await fetch(`${API}/api/investor/reports/${detail.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ki_narration: narrationEdit }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Speicher-Fehler');
      setInfo('Narration gespeichert + PDF neu gerendert.');
      await loadList();
      await loadDetail(detail.id);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function handleApprove() {
    if (!detail) return;
    if (!confirm(`Report ${monthLabel(detail.jahr, detail.monat)} final freigeben? Eine erneute Generierung würde den Status wieder auf Draft setzen.`)) return;
    setBusy(true); setError(null); setInfo(null);
    try {
      const res = await fetch(`${API}/api/investor/reports/${detail.id}/approve`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Freigabe-Fehler');
      setInfo('Report freigegeben.');
      await loadList();
      await loadDetail(detail.id);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  function handleDownloadPdf() {
    if (!detail) return;
    window.open(`${API}/api/investor/reports/${detail.id}/pdf`, '_blank');
  }

  const monthOptions = useMemo(() => {
    const opts = [];
    const cur = lastMatureMonth();
    for (let i = 0; i < 18; i++) {
      let y = cur.jahr;
      let m = cur.monat - i;
      while (m <= 0) { m += 12; y -= 1; }
      opts.push({ jahr: y, monat: m });
    }
    return opts;
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-lg font-semibold text-[#1A1A2E]">Investor-Report</h1>
          <p className="text-xs text-[#6B7280] mt-0.5">KI-Narration auf Basis deterministischer Fact-Sheets. Anhang im PDF enthält alle Zahlen.</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={`${genJahr}-${genMonat}`}
            onChange={e => { const [y, m] = e.target.value.split('-').map(Number); setGenJahr(y); setGenMonat(m); }}
            className="text-xs border border-[#E5E7EB] rounded-md px-2 py-1.5 bg-white text-[#1A1A2E]"
          >
            {monthOptions.map(o => (
              <option key={`${o.jahr}-${o.monat}`} value={`${o.jahr}-${o.monat}`}>{monthLabel(o.jahr, o.monat)}</option>
            ))}
          </select>
          <button
            onClick={handleGenerate}
            disabled={busy}
            className="text-xs px-3 py-1.5 rounded-md bg-[#338edc] text-white font-medium disabled:opacity-60 hover:bg-[#2a7ec5]"
          >
            {busy ? 'Erzeuge…' : 'Report erzeugen'}
          </button>
        </div>
      </div>

      {error && <div className="text-xs bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md">{error}</div>}
      {info && <div className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 rounded-md">{info}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        {/* Liste */}
        <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
          <div className="px-3 py-2 border-b border-[#E5E7EB] text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold">Reports</div>
          {loadingList ? (
            <div className="px-3 py-6 text-xs text-[#6B7280]">Lade…</div>
          ) : list.length === 0 ? (
            <div className="px-3 py-6 text-xs text-[#6B7280]">Noch keine Reports.</div>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto">
              {list.map(r => (
                <button
                  key={r.id}
                  onClick={() => setActiveId(r.id)}
                  className={`w-full text-left px-3 py-2 border-b border-[#F1F2F4] hover:bg-[#F8F9FC] transition ${activeId === r.id ? 'bg-[#338edc]/5' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[#1A1A2E]">{monthLabel(r.jahr, r.monat)}</span>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="text-[10px] text-[#9CA3AF] mt-0.5">Erstellt: {fmtDate(r.created_at)}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Detail / Editor */}
        <div className="bg-white border border-[#E5E7EB] rounded-xl">
          {!detail && !loadingDetail && (
            <div className="px-6 py-12 text-center text-sm text-[#6B7280]">Wähle einen Report links oder erzeuge einen neuen oben.</div>
          )}
          {loadingDetail && <div className="px-6 py-12 text-center text-xs text-[#6B7280]">Lade Report…</div>}
          {detail && !loadingDetail && (
            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-base font-semibold text-[#1A1A2E]">{monthLabel(detail.jahr, detail.monat)}</div>
                  <div className="text-[11px] text-[#6B7280] mt-0.5">
                    Erstellt {fmtDate(detail.created_at)} · Geändert {fmtDate(detail.updated_at)}
                    {detail.approved_at ? ` · Freigegeben ${fmtDate(detail.approved_at)}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={detail.status} />
                  <button
                    onClick={handleDownloadPdf}
                    disabled={!detail.pdf_path}
                    className="text-xs px-3 py-1.5 rounded-md border border-[#E5E7EB] text-[#1A1A2E] hover:bg-[#F8F9FC] disabled:opacity-50"
                    title={detail.pdf_path ? '' : 'Keine PDF vorhanden'}
                  >
                    PDF öffnen
                  </button>
                  <button
                    onClick={handleApprove}
                    disabled={busy || detail.status === 'approved'}
                    className="text-xs px-3 py-1.5 rounded-md bg-emerald-600 text-white font-medium disabled:opacity-50 hover:bg-emerald-700"
                  >
                    {detail.status === 'approved' ? 'Freigegeben' : 'Freigeben'}
                  </button>
                </div>
              </div>

              {detail.llm_model && (
                <div className="text-[10px] text-[#9CA3AF]">
                  Modell: {detail.llm_model} · Tokens in/out: {detail.llm_tokens_input || 0}/{detail.llm_tokens_output || 0}
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-[#1A1A2E]">KI-Narration (Markdown)</label>
                  <button
                    onClick={handleSaveNarration}
                    disabled={busy || narrationEdit === (detail.ki_narration || '')}
                    className="text-xs px-2 py-1 rounded-md bg-[#338edc] text-white font-medium disabled:opacity-50 hover:bg-[#2a7ec5]"
                  >
                    {busy ? 'Speichere…' : 'Speichern + PDF neu rendern'}
                  </button>
                </div>
                <textarea
                  value={narrationEdit}
                  onChange={e => setNarrationEdit(e.target.value)}
                  rows={22}
                  className="block w-full text-xs font-mono border border-[#E5E7EB] rounded-md px-3 py-2 focus:ring-1 focus:ring-[#338edc] focus:border-[#338edc] outline-none"
                  placeholder="(noch keine Narration generiert)"
                />
                <div className="text-[10px] text-[#9CA3AF] mt-1">
                  Speichern setzt den Status zurück auf Draft. KI-Narration enthält KEINE Zahlen — diese kommen aus dem Anhang.
                </div>
              </div>

              <FactSheetPreview facts={detail.json_facts} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === 'approved') {
    return <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">Freigegeben</span>;
  }
  return <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 font-medium">Draft</span>;
}

function fmtEur(v) {
  if (v == null || isNaN(v)) return '—';
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
}

function FactSheetPreview({ facts }) {
  if (!facts || typeof facts !== 'object') return null;
  const guv = facts.guv || {};
  const seg = facts.segmente || {};
  const cf = facts.cashflow || {};
  return (
    <details className="border border-[#E5E7EB] rounded-md">
      <summary className="px-3 py-2 text-xs font-medium text-[#1A1A2E] cursor-pointer hover:bg-[#F8F9FC]">
        Fact-Sheet-Vorschau (deterministische Zahlen)
      </summary>
      <div className="p-3 space-y-3 text-xs">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Kpi label="Umsatz" value={fmtEur(guv.umsatz)} />
          <Kpi label="EBITDA" value={fmtEur(guv.ebitda)} />
          <Kpi label="Liquidität Start" value={fmtEur(cf.bank_start)} />
          <Kpi label="Liquidität Ende" value={fmtEur(cf.bank_end)} />
        </div>
        {Array.isArray(seg.rows) && seg.rows.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Segment-DB</div>
            <table className="w-full text-[11px]">
              <tbody>
                {seg.rows.map((r, i) => (
                  <tr key={i} className="border-t border-[#F1F2F4]">
                    <td className="py-1 text-[#4B5563]">{r.label}</td>
                    <td className="py-1 text-right text-[#1A1A2E] tabular-nums">{fmtEur(r.db)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <pre className="bg-[#F8F9FC] border border-[#E5E7EB] rounded-md p-2 text-[10px] overflow-x-auto max-h-64">{JSON.stringify(facts, null, 2)}</pre>
      </div>
    </details>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="border border-[#E5E7EB] rounded-md p-2">
      <div className="text-[10px] text-[#6B7280] uppercase tracking-wider">{label}</div>
      <div className="text-sm font-semibold text-[#1A1A2E] tabular-nums mt-0.5">{value}</div>
    </div>
  );
}
